import type { AxisInset } from '../data/horizontalAxes';
import type { Rgb } from './colorScale';

/**
 * One family of grid lines on the surface, in the camera's cube: lines at `first + k * spacing` for
 * `k` from 0 to `count`, each interval split into `divisions` by lighter minor lines. Evenly spaced
 * because the axis ticks are, so four numbers describe a family exactly and the GPU needs no array.
 */
export type DrapedGridAxis = {
  first: number;
  spacing: number;
  count: number;
  /** Minor intervals per tick interval; 1 draws no minor lines. */
  divisions: number;
};

/**
 * The axis grid drawn onto the surface itself, at the ticks the floor grid rules.
 *
 * Unlike the mesh lines it is regular and hidden where the surface hides it: each line is a value on
 * an axis, not an edge of the triangulation, and a ridge in front covers the lines behind it.
 */
export type DrapedGrid = {
  x: DrapedGridAxis;
  y: DrapedGridAxis;
  /** The ink over a light stretch of surface, and over a dark one. */
  inkOnLight: Rgb;
  inkOnDark: Rgb;
  opacity: number;
};

/**
 * Lightness, 0 to 1, above which a surface takes the ink for light surfaces.
 *
 * The surface is the color ramp, which is the same in either theme, so one ink loses against part
 * of it whatever the theme: a light ink vanishes over the pale end, a dark one over the deep end.
 */
export const GRID_INK_LIGHTNESS_SPLIT = 0.5;

/** A color's lightness, 0 to 1, from its 0-255 channels weighted as the eye weighs them. */
export const toLightness = ([red, green, blue]: readonly number[]): number =>
  (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

/** The ink a grid line takes over a surface of this color. */
export const toGridInk = (
  surface: readonly number[],
  { inkOnLight, inkOnDark }: Pick<DrapedGrid, 'inkOnLight' | 'inkOnDark'>
): Rgb =>
  toLightness(surface) > GRID_INK_LIGHTNESS_SPLIT ? inkOnLight : inkOnDark;

/** The theme's two extremes as the grid's inks: the darker over light surface, the lighter over dark. */
export const toGridInks = (
  inkColor: Rgb,
  surfaceColor: Rgb
): Pick<DrapedGrid, 'inkOnLight' | 'inkOnDark'> =>
  toLightness(inkColor) <= toLightness(surfaceColor)
    ? { inkOnLight: inkColor, inkOnDark: surfaceColor }
    : { inkOnLight: surfaceColor, inkOnDark: inkColor };

/** How strongly the grid is inked. Lighter than the outlines, which it sits under. */
export const DRAPED_GRID_OPACITY = 0.35;

/**
 * Where a line stops counting as on a pixel, in pixels from its center: full ink within the first
 * quarter pixel and none past one, a line about a pixel wide with smoothed edges.
 */
export const GRID_LINE_CORE = 0.25;
export const GRID_LINE_EDGE = 1;

/** How strongly a minor line is inked, as a share of a tick line's ink. */
export const MINOR_GRID_INK = 0.4;

/**
 * Minor line spacing on screen, in pixels, below which minor lines fade out and at which they are
 * fully drawn, so they thin away where the surface foreshortens rather than filling it with ink.
 */
export const MINOR_GRID_FADE_START = 4;
export const MINOR_GRID_FADE_END = 10;

/**
 * Minor intervals for a tick step, chosen so each minor line falls on a round value: a step of 1,
 * 2.5 or 5 splits in five, 2 in four. Any other step has no round split and gets no minor lines.
 */
export const toMinorDivisions = (step: number): number => {
  if (!Number.isFinite(step) || step <= 0) return 1;
  const mantissa = step / 10 ** Math.floor(Math.log10(step));
  const near = (value: number): boolean => Math.abs(mantissa - value) < 1e-6;
  if (near(2)) return 4;
  if ([1, 2.5, 5, 10].some(near)) return 5;
  return 1;
};

/** The family for one axis, from the tick layout the floor grid and labels use. */
export const toDrapedGridAxis = (
  tickCount: number,
  [lowInset, highInset]: AxisInset,
  divisions: number
): DrapedGridAxis => ({
  first: lowInset * 2 - 1,
  spacing: tickCount > 0 ? (2 * (1 - lowInset - highInset)) / tickCount : 0,
  count: Math.max(0, tickCount),
  divisions: Math.max(1, Math.round(divisions)),
});

const smoothstep = (edge0: number, edge1: number, value: number): number => {
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

/**
 * How much of a pixel the nearest line of a family covers, 0 to 1.
 *
 * `footprint` is how far the coordinate moves across one pixel - the shader's `fwidth` - so the line
 * keeps its width in pixels however far the camera zooms. A coordinate past the first or last tick
 * is measured to that end line and no further, so the grid stops where the axis does.
 */
export const toGridLineCoverage = (
  coordinate: number,
  { first, spacing, count }: DrapedGridAxis,
  footprint: number
): number => {
  if (spacing <= 0 || footprint <= 0) return 0;
  const nearest = Math.min(
    count,
    Math.max(0, Math.round((coordinate - first) / spacing))
  );
  const distance =
    Math.abs(coordinate - (first + nearest * spacing)) / footprint;
  return 1 - smoothstep(GRID_LINE_CORE, GRID_LINE_EDGE, distance);
};

/**
 * How much ink a family puts on a pixel, 0 to 1: its tick lines at full strength and its minor
 * lines at `MINOR_GRID_INK`, faded by how closely they crowd on screen.
 */
export const toDrapedGridCoverage = (
  coordinate: number,
  axis: DrapedGridAxis,
  footprint: number
): number => {
  const major = toGridLineCoverage(coordinate, axis, footprint);
  if (axis.divisions <= 1 || footprint <= 0) return major;
  const minorSpacing = axis.spacing / axis.divisions;
  const crowding = smoothstep(
    MINOR_GRID_FADE_START,
    MINOR_GRID_FADE_END,
    minorSpacing / footprint
  );
  const minor = toGridLineCoverage(
    coordinate,
    { ...axis, spacing: minorSpacing, count: axis.count * axis.divisions },
    footprint
  );
  return Math.max(major, minor * MINOR_GRID_INK * crowding);
};
